import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmViolationType } from 'APIPackage/CatalogsModule/interfaces/IKnmViolationType';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { getCatalogKnmViolationTypesAll } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminGetCatalogTypes, adminCreateCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type KnmViolationTypeForm = Omit<IKnmViolationType, 'id'>;

interface IKnmViolationTypeEditFormProps {
  onBack: () => void;
}

interface IKnmViolationTypeEditFormStoreProps {
  knmViolationTypes: IKnmViolationType[];
}

const KnmViolationTypeEditFormWithoutStore = ({
  onBack,
  knmViolationTypes,
}: IKnmViolationTypeEditFormProps & IKnmViolationTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedKnmViolationType, setSelectedKnmViolationType] = useState<IKnmViolationType | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const knmViolationType = knmViolationTypes.find(k => k.id.toString() === id);

      setSelectedKnmViolationType(prev => (prev?.id === knmViolationType?.id ? undefined : knmViolationType));
    },
    [knmViolationTypes],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES_ALL));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedKnmViolationType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
      weight: 0,
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedKnmViolationType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: KnmViolationTypeForm) => {
      if (isNew) {
        dispatch(
          adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES_ALL }),
        );
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedKnmViolationType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.KNM_VIOLATION_TYPES_ALL,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedKnmViolationType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<KnmViolationTypeForm>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Вес" color="black">
            <TextInputField min={1} max={999999999} type="number" name="weight" />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<KnmViolationTypeForm> onSubmit={onSubmit} initialValues={selectedKnmViolationType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Характеры выявленных нарушений</span>
        {knmViolationTypes &&
          knmViolationTypes.map((knmViolationType: IKnmViolationType) => (
            <button
              key={knmViolationType.id}
              className={selectedKnmViolationType?.id === knmViolationType.id ? styles.DataRowSelected : styles.DataRow}
              data-id={knmViolationType.id}
              onClick={onRowClick}
              type="button"
            >
              {knmViolationType.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedKnmViolationType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmViolationTypeEditFormStoreProps => ({
  knmViolationTypes: getCatalogKnmViolationTypesAll(state),
});

export const KnmViolationTypeEditForm = connect(mapStateToProps)(KnmViolationTypeEditFormWithoutStore);
